/**
 * Race & plan settings (design 1j, build-spec T11) — PLAN-scoped things only
 * (fueling-profile plan D5/P2.4): race context, training method, drop cadence,
 * the race-day carb target, archive. Athlete-level fueling (sodium, gut, the
 * gut-training ramp, per-sport targets) is edited ONLY in Preferences →
 * Fueling; this modal shows a status row that links there (close-then-open —
 * never stacked, 390px). Changes apply from the next drop; purchased drops are
 * frozen server-side.
 */
import { useState } from "react";
import { addDaysYmd, compareYmd } from "../../lib/dates";
import { ModalShell } from "../modals/ModalShell";
import { LucideIcon } from "../FG/LucideIcon";
import { DateField } from "../ui/DateField";
import { WindowPhasebar } from "../Planner/RacePlanWindowConfirm";
import { useAuth } from "../../context/AuthProvider";
import { ApiError } from "../../lib/api";
import {
  getJourneyView,
  journeyRaceName,
  notifyJourneyChanged,
  patchJourney,
  windowCoversFromError,
  windowOrphansFromError,
  goalArchiveTuneupsFromError,
  type CustomTargets,
  type JourneyMilestone,
  type JourneyPatch,
  type JourneyView
} from "../../lib/journeyApi";
import { daysBetweenYmd } from "../../lib/journeyWindow";
import { clampCarbTarget, clampSodiumTarget } from "../../lib/fuelClamps";
import { CADENCE_QUESTION } from "../../lib/questionCopy";
import {
  RoleResolutionSheet,
  type RoleResolutionContext,
  type RoleResolutionChoice
} from "../Planner/RoleResolutionSheet";
import { EventDateEditModal } from "../Planner/EventDateEditModal";
import { useModal } from "../../context/ModalProvider";
import "./journeyRail.css";

function fmtLong(ymd: string): string {
  return new Date(`${ymd}T00:00:00Z`).toLocaleDateString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
    timeZone: "UTC"
  });
}

const DISTANCE_LABEL: Record<string, string> = {
  marathon: "Marathon",
  half: "Half marathon",
  "10k": "10K",
  custom: "Custom"
};

// Carb / sodium clamps live in ../../lib/fuelClamps (clampCarbTarget /
// clampSodiumTarget) so a server-limit change lands in one place. The band
// and 0 = unset semantics are identical to the previous inline copies.
const clampCarb = clampCarbTarget;
const clampSodium = clampSodiumTarget;

export function JourneySettingsModal({
  journey,
  onClose,
  onBack,
  onSwapMethod,
  onEditGoal,
  onOpenFuelingProfile,
  onPromoteMilestone,
  onRemoveMilestone,
  onDemoteAnchor,
  onSwitchToRolling,
  raceEventCatalogId
}: {
  journey: JourneyView;
  onClose: () => void;
  /** Sub-modal back (e.g. return to Preferences menu). Omitted on timeline/plan surfaces. */
  onBack?: () => void;
  /** Close settings and open the apply-plan wizard (Change race / Swap method). */
  onSwapMethod?: () => void;
  /** Close settings and reopen the apply-plan wizard AT THE GOAL STEP with this
   *  journey's race preloaded (goal-time edit). Changing the goal re-applies the
   *  plan — the wizard owns the replace confirm (upcoming sessions swap to the
   *  new paces; past weeks and fueled boxes stay). Undefined ⇒ the Goal time
   *  pair renders display-only. */
  onEditGoal?: () => void;
  /** Close settings and open Preferences → Fueling (athlete-level editor, D5). */
  onOpenFuelingProfile?: () => void;
  /** A4 (spec §9.4/§10): promote a nested tune-up back to its own goal race
   *  (milestone → goal) from the "Tune-ups in this build" list. Undefined ⇒ the
   *  row renders no Promote action (display-only, no C2 dep). The HOST maps this
   *  to C2's `useReclassifyEvent`/`applyRoleResolution` (§2.2), which opens
   *  `RoleResolutionSheet` when the promote collides (SF18). Mirrors the
   *  A5 `BoxTimelineView` contract so a caller can wire one place and both. */
  onPromoteMilestone?: (milestone: JourneyMilestone) => void;
  /** A4: remove a nested tune-up event entirely from the list. Undefined ⇒ no
   *  Remove action renders. The host maps this to the event-delete path (kit
   *  cleanup lives server-side, S4/T6). Kept separate from promote so callers
   *  can wire one without the other. */
  onRemoveMilestone?: (milestone: JourneyMilestone) => void;
  /** Lane D (add-event surfacing §5 Lane D): demote THIS journey's own anchoring
   *  goal race to a tune-up (goal → milestone). Renders a subtle "Make this a
   *  tune-up" action in the Race section, but ONLY on a non-rolling journey that
   *  carries a `customerEventId` (anon race plans have none — the reclassify
   *  endpoint is customer-scoped). Mirrors `onPromoteMilestone`: the modal stays
   *  presentational, the HOST maps this to `useEventRoleActions.changeEventRole`
   *  ({ from:"goal", to:"milestone" }) which already handles the 409 collision
   *  sheet, the 422 no-containing-build toast, and CSRF. The modal closes settings
   *  before firing so the host's sheet/toast never stacks over it (R7). Undefined
   *  ⇒ no demote action. */
  onDemoteAnchor?: () => void;
  /** Lane G (add-event surfacing §5 Lane G — "don't lock people in"): drop the
   *  race but keep fueling training (→ rolling). Renders a "Switch to just
   *  training →" action beside "Archive" (race journeys only). It runs the SAME
   *  archive flow as the plain Archive (including the nested-tune-up `goal_removal`
   *  disposition sheet), but on a SUCCESSFUL archive calls this instead of a plain
   *  close, so the host can ensure a rolling journey (`createRollingJourney`,
   *  idempotent) + toast. Undefined ⇒ only the plain Archive renders. */
  onSwitchToRolling?: () => void;
  /** F1 (spec §6.1): the anchoring event's linked catalog row id, or
   *  null/absent for a CUSTOM event. Race dates are custom-only edits (catalog
   *  dates are shared rows) — the race row's "Edit date" affordance gates on
   *  this being null/absent AND `customerEventId` present. The server 422
   *  (`catalog_event_date_immutable`) backstops a stale/missing value. */
  raceEventCatalogId?: string | null;
}) {
  const { state, refreshCsrf } = useAuth();
  const csrfToken = (state as { csrfToken?: string }).csrfToken ?? "";
  const [dropWeeks, setDropWeeks] = useState(journey.dropWeeks ?? 2);
  // T3b race-day anchor — the one carb target that stays per-plan (goal-
  // dependent; per-sport targets moved to the fueling profile). 0/"" = unset.
  const ct = journey.customTargets ?? {};
  // P2 input polish: string-backed so "" doesn't snap to 0 mid-edit; we clamp
  // on blur (commit) and flag when a typed value was truncated so the change
  // isn't silent. `Number("")` is 0, which is our "unset" sentinel — consistent
  // with the old numeric state.
  const [raceDayAnchor, setRaceDayAnchor] = useState(
    ct.raceDayGramsPerHour ? String(ct.raceDayGramsPerHour) : ""
  );
  const [raceDayAnchorClamped, setRaceDayAnchorClamped] = useState(false);
  // A5 — event-scoped race-day sodium override (mg/hr). 0/"" = unset (falls back
  // to the athlete sodium target). Rides through `customTargets.sodium.raceDay`.
  const [raceDaySodium, setRaceDaySodium] = useState(
    ct.sodium?.raceDay ? String(ct.sodium.raceDay) : ""
  );
  const [raceDaySodiumClamped, setRaceDaySodiumClamped] = useState(false);
  const raceDayAnchorNum = Number(raceDayAnchor) || 0;
  const raceDaySodiumNum = Number(raceDaySodium) || 0;
  // ONE stop surface: "Stop this race plan…" opens a chooser that makes the
  // two outcomes explicit (keep fueling → rolling vs stop entirely → archive).
  // Rolling journeys (no race to drop) get a plain stop-fueling confirm latch.
  const [stopOpen, setStopOpen] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(false);
  const [saving, setSaving] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // ── Role-conflict resolution (C3, spec §9.2/§9.3) ───────────────────────
  // A window edit or archive that pulls in / orphans another event opens the
  // shared `RoleResolutionSheet`. We keep the BASE patch we were mid-flight on
  // plus any resolution keys accumulated so far, so re-running after the sheet
  // re-sends one PATCH carrying both the window/status change AND the answer.
  // `pending` also names which flow is running so we restore the right busy
  // flag when the sheet is cancelled.
  // `pendingFlow` also names which flow is running so we restore the right busy
  // flag when the sheet is cancelled. Lane G adds `switch_rolling` — it behaves
  // exactly like `archive` (same busy flag, same `{ status: "archived" }` PATCH,
  // same nested-tune-up `goal_removal` disposition sheet) EXCEPT that a
  // successful archive calls `onSwitchToRolling` (drop the race, keep fueling)
  // instead of a plain close.
  const [roleContext, setRoleContext] = useState<RoleResolutionContext | null>(null);
  const [pendingPatch, setPendingPatch] = useState<JourneyPatch | null>(null);
  const [pendingFlow, setPendingFlow] = useState<
    "window" | "archive" | "switch_rolling" | null
  >(null);

  // Rolling variant (P3, surfaces audit §2.9): no Race section, no race-day
  // target — title "Training journey settings"; cadence + archive stay.
  const isRolling = journey.journeyType === "rolling";
  const raceName = journeyRaceName(journey);

  // Sub-dialogs go through the GLOBAL modal stack so they layer over this
  // settings modal with their own scrim — an inline `<ModalShell>` here would
  // render as an in-flow box BELOW the settings shell (the old edit-date bug).
  const { open: openStacked, close: closeStacked } = useModal();

  // F1 (spec §6.1) — race-date edit for a CUSTOM anchoring event. On success it
  // fires `notifyJourneyChanged` itself, so the host's journey refetch updates
  // this modal's race row.
  const openDateEdit = () => {
    if (!journey.customerEventId) return;
    const id = openStacked(
      <EventDateEditModal
        event={{
          id: journey.customerEventId,
          name: raceName,
          dateYmd: journey.raceYmd ?? "",
          eventCatalogId: raceEventCatalogId ?? null
        }}
        onSaved={() => closeStacked(id)}
        onClose={() => closeStacked(id)}
      />
    );
  };

  // "Make this a prep race" — a real consequence (this race's own plan ends),
  // so it confirms with context before firing the host's demote handler.
  const openDemoteConfirm = () => {
    if (!onDemoteAnchor) return;
    const id = openStacked(
      <DemotePrepRaceConfirm
        raceName={raceName}
        onCancel={() => closeStacked(id)}
        onConfirm={() => {
          closeStacked(id);
          onDemoteAnchor();
        }}
      />
    );
  };

  const distance = journey.planDistance ? DISTANCE_LABEL[journey.planDistance] : null;

  // ── Nested tune-ups (A4, spec §9.4/§10) ────────────────────────────────
  // The milestones riding inside this build's window. Rolling journeys have no
  // window so never nest tune-ups; the "Tune-ups in this build" section only
  // renders on a race journey that actually has some, and only when the host
  // wired at least one manage action (promote/remove — a C2 dep).
  const milestones = isRolling ? [] : journey.milestones ?? [];
  const hasTuneupActions = !!(onPromoteMilestone || onRemoveMilestone);
  const showTuneupList = milestones.length > 0 && hasTuneupActions;

  // ── Window editing (P5, J2 — surfaces audit §2.9) ──────────────────────
  // Free-built race plans edit "Plan start" directly; journeys applied from a
  // library plan have a plan-defined window, so their "Edit" only moves the
  // start EARLIER (the plan's workouts don't move — earlier weeks are fueled
  // but start empty; the server 409s anything else). Race day is NEVER edited
  // here (it belongs to the event). Rolling journeys have no window section.
  const isLibraryPlan = !!journey.trainingPlanId;
  const windowEditable = !isRolling && !!journey.raceYmd && !!journey.planStartYmd;
  const [startYmd, setStartYmd] = useState(journey.planStartYmd ?? "");
  // Library plans keep the value read-only until "Edit" swaps in the (earlier-
  // only) date field — replaces the old "Include earlier weeks…" special case.
  const [editStart, setEditStart] = useState(false);
  const startChanged = windowEditable && startYmd !== "" && startYmd !== journey.planStartYmd;
  // Live phasebar preview — same generic build/peak/taper grammar as the
  // wizard's window step (D2), re-sliced as the start date moves.
  const previewWeeks =
    windowEditable && journey.raceYmd && startYmd
      ? Math.max(1, Math.ceil((daysBetweenYmd(startYmd, journey.raceYmd) + 1) / 7))
      : null;

  // ATHLETE fueling status (read-only here) — server-computed position (F2/D9).
  // C11: print the server's canonical `rampWeekLabel` verbatim when present;
  // only compose the fallback string from the counts on older server builds
  // that don't send it (never re-derive week N from a date).
  const rampStatus = !journey.rampEnabled
    ? "Gut ramp off"
    : journey.rampState === "pending"
      ? "Gut ramp starting soon"
      : journey.rampState === "full"
        ? "Gut ramp at full fuel"
        : journey.rampWeekLabel && journey.rampWeekLabel.trim()
          ? journey.rampWeekLabel
          : journey.rampWeekNow != null
            ? `Gut-training week ${journey.rampWeekNow} of ${journey.rampWeeksTotal}`
            : "Gut ramp on";
  const fuelingStatus = [
    rampStatus,
    journey.sodiumTarget && journey.sodiumTarget > 0
      ? `Sodium ${journey.sodiumTarget} mg/hr`
      : null
  ]
    .filter(Boolean)
    .join(" · ");

  /** Map a `RoleResolutionChoice` (from the sheet) onto an accumulating PATCH
   *  body (C3, §2.2 — the window/archive rows). Merges into the resolution
   *  arrays so several rows can be answered before one re-send. */
  function mergeChoiceIntoPatch(base: JourneyPatch, choice: RoleResolutionChoice): JourneyPatch {
    const next: JourneyPatch = { ...base };
    if (choice.kind === "window_covers") {
      if (choice.action === "reclassify") {
        next.reclassify = [
          ...(next.reclassify ?? []),
          { eventId: choice.eventId, to: "milestone" }
        ];
      } else if (choice.action === "nest_existing") {
        next.pairResolution = [
          ...(next.pairResolution ?? []),
          { eventId: choice.eventId, mode: "demote" }
        ];
      } else if (choice.action === "split") {
        next.pairResolution = [
          ...(next.pairResolution ?? []),
          { eventId: choice.eventId, mode: "split" }
        ];
      } else {
        // clamp_start — "stop the window before it". Drop the wider start and
        // clamp to just before the covered event. When a batch has more than
        // one clamp_start, keep the EARLIEST clamp (stopping before the earliest
        // covered race also excludes every later one).
        const clampYmd = addDaysYmd(choiceEventYmd(choice.eventId), -1);
        next.clampStartYmd =
          next.clampStartYmd && compareYmd(next.clampStartYmd, clampYmd) < 0
            ? next.clampStartYmd
            : clampYmd;
        delete next.startYmd;
      }
    } else if (choice.kind === "window_orphans") {
      if (choice.action === "extend") {
        // "Extend to keep it" — widen the window back so the tune-up stays in.
        // Send the tune-up's day as the (earlier-or-equal) start; the server
        // clamps to a valid window.
        next.clampStartYmd = choiceEventYmd(choice.eventId);
        delete next.startYmd;
      } else if (choice.action === "keep_as_goal") {
        next.reclassify = [
          ...(next.reclassify ?? []),
          { eventId: choice.eventId, to: "goal" }
        ];
      } else {
        // remove — one disposition applies to the whole shrink; the server
        // deletes the orphaned tune-ups + their kits.
        next.tuneupDisposition = "remove";
      }
    } else if (choice.kind === "goal_removal") {
      next.tuneupDisposition = choice.disposition;
    }
    return next;
  }

  /** Resolve a covered/orphaned event id → its YMD, from whatever we know
   *  (the open sheet context or the journey's milestones). Falls back to the
   *  journey start so a clamp never sends an empty string. */
  function choiceEventYmd(eventId: string): string {
    if (roleContext?.kind === "window_covers") {
      const ev = roleContext.events.find((e) => e.id === eventId);
      if (ev?.ymd) return ev.ymd;
    }
    if (roleContext?.kind === "window_orphans") {
      const tu = roleContext.tuneups.find((t) => t.id === eventId);
      if (tu?.ymd) return tu.ymd;
    }
    const m = (journey.milestones ?? []).find((mi) => mi.eventId === eventId);
    return m?.ymd ?? journey.planStartYmd ?? startYmd;
  }

  /** Build the base PATCH the Save button sends: cadence + merged customTargets
   *  + (only when moved) the window start. Re-reads customTargets so sibling
   *  edits survive (C6). */
  async function buildSavePatch(): Promise<JourneyPatch> {
    // C6: re-read the CURRENT journey right before writing — this modal
    // snapshots the journey at open, and a sibling save (per-session chip, a
    // per-sport edit that rewrote sodium, another surface's race-day change)
    // made meanwhile would be silently clobbered if we rebuilt customTargets
    // from the open-time snapshot. Start from the FRESH customTargets and
    // apply ONLY the keys this modal owns, so unknown/sibling keys survive.
    let freshTargets: CustomTargets = { ...(ct ?? {}) };
    try {
      const fresh = await getJourneyView(journey.id);
      if (fresh) freshTargets = { ...(fresh.customTargets ?? {}) };
    } catch {
      /* fall back to the snapshot — never block the save on the re-read */
    }
    // Merge, don't replace: mutate a copy of the fresh targets by only the
    // fields this modal edits (race-day carb anchor + race-day sodium).
    const nextTargets: CustomTargets = { ...freshTargets };
    if (clampCarb(raceDayAnchorNum) > 0) {
      nextTargets.raceDayGramsPerHour = clampCarb(raceDayAnchorNum);
    } else {
      // Cleared here → drop the key so the plan falls back to policy.
      delete nextTargets.raceDayGramsPerHour;
    }
    // A5 — race-day sodium override rides through `customTargets.sodium.raceDay`.
    // The `sodium` block currently holds ONLY `raceDay` (event-scoped), so we
    // rewrite/clear it wholesale; if the shape ever gains siblings, merge here.
    if (clampSodium(raceDaySodiumNum) > 0) {
      nextTargets.sodium = {
        ...(nextTargets.sodium ?? {}),
        raceDay: clampSodium(raceDaySodiumNum)
      };
    } else {
      delete nextTargets.sodium;
    }
    return {
      dropWeeks,
      customTargets: nextTargets,
      // Window edit (P5, J2) — only when actually moved; raceYmd is never
      // sent from here (race day belongs to the event).
      ...(startChanged ? { startYmd } : {})
    };
  }

  /**
   * Send a PATCH; on an S3 window-edit 409 open the shared `RoleResolutionSheet`
   * so the user answers, then re-send the same PATCH with the resolution keys
   * merged in (SF20 — the server is the trust boundary; the sheet is the
   * backstop even when the client pre-check missed a covered event). Any other
   * error surfaces inline via `onError`.
   */
  async function commitPatch(
    patch: JourneyPatch,
    flow: "window" | "archive" | "switch_rolling",
    onError: (msg: string) => void
  ): Promise<void> {
    try {
      await patchJourney(journey.id, patch, csrfToken, refreshCsrf);
      notifyJourneyChanged();
      // Lane G: on a "switch to just training" archive, hand off to the host so
      // it can ensure a rolling journey (createRollingJourney, idempotent) +
      // toast, INSTEAD of a plain close. The host owns the close in that path
      // (it runs after the rolling create resolves). The plain archive / window
      // flows just close here as before.
      if (flow === "switch_rolling" && onSwitchToRolling) {
        onSwitchToRolling();
        return;
      }
      onClose();
    } catch (err) {
      // S3 role-conflict backstops (spec §9.2/§9.3): open the sheet, stash the
      // in-flight patch, and let `onRoleResolve` re-send with the answer.
      const covers = windowCoversFromError(err);
      if (covers) {
        setPendingPatch(patch);
        setPendingFlow(flow);
        setRoleContext({
          kind: "window_covers",
          journeyId: covers.journeyId,
          events: covers.events.map((e) => ({
            id: e.id,
            name: e.name,
            ymd: e.ymd,
            hasJourney: e.hasJourney
          }))
        });
        setSaving(false);
        setRemoving(false);
        return;
      }
      const orphans = windowOrphansFromError(err);
      if (orphans) {
        setPendingPatch(patch);
        setPendingFlow(flow);
        setRoleContext({
          kind: "window_orphans",
          journeyId: orphans.journeyId,
          tuneups: orphans.events.map((e) => ({
            id: e.id,
            name: e.name,
            ymd: e.ymd,
            hasJourney: false,
            hasPurchasedKit: e.hasPurchasedKit
          }))
        });
        setSaving(false);
        setRemoving(false);
        return;
      }
      const archiveConflict = goalArchiveTuneupsFromError(err);
      if (archiveConflict) {
        setPendingPatch(patch);
        setPendingFlow(flow);
        setRoleContext({
          kind: "goal_removal",
          goal: {
            id: journey.customerEventId ?? journey.id,
            name: raceName,
            ymd: journey.raceYmd ?? "",
            hasJourney: true
          },
          tuneups: archiveConflict.tuneups.map((t) => ({
            id: t.id,
            name: t.name,
            ymd: t.ymd,
            hasJourney: false
          }))
        });
        setSaving(false);
        setRemoving(false);
        return;
      }
      // J2 guards, inline: the server is the trust boundary for library-plan
      // windows (only an earlier start is allowed) and window inversion.
      if (err instanceof ApiError && err.code === "window_locked") {
        onError(
          "This plan's window comes from your training plan — you can only include earlier weeks."
        );
      } else if (err instanceof ApiError && err.code === "invalid_window") {
        onError("The plan start must be on or before race day.");
      } else {
        onError(
          flow === "archive" || flow === "switch_rolling"
            ? "Couldn't remove — try again."
            : "Couldn't save — try again."
        );
      }
      setSaving(false);
      setRemoving(false);
    }
  }

  async function save() {
    // P2 in-flight guard: `aria-disabled` doesn't block the click handler, so a
    // double-tap could fire two PATCHes (and two `onClose`s). Bail if a save is
    // already running.
    if (saving) return;
    setSaving(true);
    setError(null);
    // Pre-check (C3): a SHRINK that pushes a nested tune-up outside the new
    // window opens the re-home sheet before the PATCH. Covers (extend) need the
    // full event list the modal doesn't hold — those come back as the 409
    // backstop in `commitPatch`. Rolling/library edits can't shrink here.
    if (startChanged && journey.raceYmd) {
      const orphaned = (journey.milestones ?? []).filter(
        (m) => compareYmd(m.ymd, startYmd) < 0 || compareYmd(m.ymd, journey.raceYmd!) >= 0
      );
      if (orphaned.length > 0) {
        setPendingPatch(await buildSavePatch());
        setPendingFlow("window");
        setRoleContext({
          kind: "window_orphans",
          journeyId: journey.id,
          tuneups: orphaned.map((m) => ({
            id: m.eventId,
            name: m.name,
            ymd: m.ymd,
            hasJourney: false,
            hasPurchasedKit: !!m.previewPlanId
          }))
        });
        setSaving(false);
        return;
      }
    }
    await commitPatch(await buildSavePatch(), "window", setError);
  }

  /**
   * Archive this journey. `flow` distinguishes the plain "Archive" (stop fueling
   * entirely) from Lane G's "Switch to just training" (drop the race, then the
   * host ensures a rolling journey). Both run the IDENTICAL archive path — same
   * `{ status: "archived" }` PATCH and the same nested-tune-up `goal_removal`
   * disposition pre-check/sheet — so they never diverge; only the on-success
   * hand-off differs (`commitPatch` calls `onSwitchToRolling` for the switch).
   */
  async function archiveJourney(flow: "archive" | "switch_rolling") {
    // P2 in-flight guard: the confirm button is `disabled` while removing, but
    // guard the handler too so a stray double-invoke can't double-archive.
    if (removing) return;
    setRemoving(true);
    setError(null);
    // Pre-check (C3, Gap 1): archiving a goal build with nested tune-ups opens
    // the disposition sheet before the archive PATCH — never silently orphan
    // them. The server 409 `goal_archive_tuneups` is the backstop for a stale
    // client (SF20).
    const tuneups = journey.milestones ?? [];
    if (!isRolling && tuneups.length > 0) {
      setPendingPatch({ status: "archived" });
      setPendingFlow(flow);
      setRoleContext({
        kind: "goal_removal",
        goal: {
          id: journey.customerEventId ?? journey.id,
          name: raceName,
          ymd: journey.raceYmd ?? "",
          hasJourney: true
        },
        tuneups: tuneups.map((m) => ({
          id: m.eventId,
          name: m.name,
          ymd: m.ymd,
          hasJourney: false
        }))
      });
      setRemoving(false);
      return;
    }
    await commitPatch({ status: "archived" }, flow, setError);
  }

  /** The sheet resolved: merge the pick into the pending PATCH and re-send. */
  async function onRoleResolve(choices: RoleResolutionChoice[]): Promise<void> {
    const base = pendingPatch ?? {};
    const flow = pendingFlow ?? "window";
    // Fold every row's choice into ONE patch (window_covers Apply sends the
    // whole batch; the single-decision kinds send a 1-element array).
    const next = choices.reduce(mergeChoiceIntoPatch, base);
    setRoleContext(null);
    setPendingPatch(null);
    setPendingFlow(null);
    // Both archive flows (plain + Lane G switch-to-rolling) use the `removing`
    // busy flag; only a window edit uses `saving`.
    if (flow === "archive" || flow === "switch_rolling") setRemoving(true);
    else setSaving(true);
    setError(null);
    await commitPatch(next, flow, setError);
  }

  /** Cancel the sheet — abandon the in-flight edit, restore the idle state. */
  function onRoleCancel(): void {
    setRoleContext(null);
    setPendingPatch(null);
    setPendingFlow(null);
    setSaving(false);
    setRemoving(false);
  }

  return (
    <>
    <ModalShell
      eyebrow={isRolling ? "Your training journey" : `${raceName} race plan`}
      title={isRolling ? "Training journey settings" : "Race & plan settings"}
      onClose={onClose}
      onBack={onBack}
      width={560}
      footerSpread
      footer={
        <>
          <button type="button" className="fg-btn fg-btn--soft fg-btn--sm" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="fg-btn fg-btn--primary fg-btn--sm"
            aria-disabled={saving}
            onClick={() => void save()}>
            {saving ? "Saving…" : "Save changes"}
          </button>
        </>
      }>
      <div className="fgjs">
        {/* ─── Race (window fields N/A on rolling journeys — no endpoint) ─── */}
        {!isRolling && (
        <>
        <div className="fgjs__section">Race</div>
        <div className="fgjs__row">
          <span className="fgjs__row-icon">
            <LucideIcon name="flag" size={15} color="var(--fg-purple)" />
          </span>
          <div className="fgjs__row-main">
            <div className="fgjs__row-title">
              {raceName}
              {journey.raceYmd ? ` · ${fmtLong(journey.raceYmd)}` : ""}
              {distance ? ` · ${distance}` : ""}
            </div>
          </div>
          <div className="fgjs__row-actions">
            {onSwapMethod && (
              <button type="button" className="fgjs__row-action" onClick={onSwapMethod}>
                Change race
              </button>
            )}
            {/* F1 (spec §6.1): date edit — CUSTOM events only (catalog dates are
                shared rows; those journeys get NO affordance) and only when the
                journey carries a `customerEventId` to PATCH (anon race plans
                have none). */}
            {journey.customerEventId && !raceEventCatalogId && journey.raceYmd && (
              <button
                type="button"
                className="fgjs__row-action"
                aria-label={`Edit ${raceName}'s race date`}
                onClick={openDateEdit}>
                Edit date
              </button>
            )}
            {/* Lane D — demote THIS journey's anchoring goal race to a tune-up
                (goal → milestone). Customer-only: the reclassify endpoint is
                customer-scoped, and an anon race plan carries no `customerEventId`
                to send. The host owns the collision/no-containing-build handling
                (useEventRoleActions) and closes settings first (R7). */}
            {onDemoteAnchor && journey.customerEventId && (
              <button
                type="button"
                className="fgjs__row-action"
                aria-label={`Make ${raceName} a prep race instead of your goal race`}
                onClick={openDemoteConfirm}>
                Make this a prep race
              </button>
            )}
          </div>
        </div>
        <div className="fgjs__pairrow">
          <div className="fgjs__pair fgjs__pair--plain">
            <span className="fgjs__pair-label">
              Goal time
              {/* Goal-time edit → wizard Goal step (host-wired). Changing the
                  goal re-applies the plan at the new paces; the wizard owns
                  the replace confirm. Close-then-open (R7). */}
              {onEditGoal && !isRolling && journey.goalFinish && (
                <button
                  type="button"
                  className="fgjs__row-action fgjs__row-action--inline"
                  aria-label={`Edit your goal time — changing it re-applies your plan's workouts at the new paces`}
                  onClick={onEditGoal}>
                  Edit
                </button>
              )}
            </span>
            <span className="fgjs__pair-value">{journey.goalFinish ?? "—"}</span>
          </div>
          <div className="fgjs__pair fgjs__pair--plain">
            <span className="fgjs__pair-label">
              Plan starts
              {/* P5/J2: library plans reveal an (earlier-only) date field on
                  Edit — replaces the "Include earlier weeks…" special case. */}
              {windowEditable && isLibraryPlan && !editStart && (
                <button
                  type="button"
                  className="fgjs__row-action fgjs__row-action--inline"
                  aria-label="Edit plan start date — you can start earlier"
                  onClick={() => setEditStart(true)}>
                  Edit
                </button>
              )}
            </span>
            {windowEditable && (!isLibraryPlan || editStart) ? (
              <DateField
                value={startYmd}
                onChange={setStartYmd}
                max={(isLibraryPlan ? journey.planStartYmd : journey.raceYmd) ?? undefined}
                showLongFormat={false}
                valuePresentation="usSlash"
                className="fgjs__startfield"
              />
            ) : (
              <span className="fgjs__pair-value">
                {journey.planStartYmd ? fmtLong(journey.planStartYmd) : "—"}
              </span>
            )}
          </div>
        </div>
        {windowEditable && isLibraryPlan && editStart && (
          <div className="fgp-modal-footnote">
            You can start earlier — earlier weeks are fueled but start empty. Add
            workouts to them from your calendar; your plan&apos;s workouts don&apos;t move.
          </div>
        )}
        {/* Live window preview (D2 grammar) + the re-slice promise (kills I6). */}
        {windowEditable && (!isLibraryPlan || editStart) && previewWeeks != null && (
          <>
            <WindowPhasebar weeks={previewWeeks} />
            <p className="fgp-window-caption">
              {fmtLong(startYmd)} → race day · {previewWeeks}{" "}
              {previewWeeks === 1 ? "week" : "weeks"}
            </p>
            <div className="fgp-modal-footnote">
              Re-slices from your next box — fueled boxes stay as ordered.
            </div>
          </>
        )}
        </>
        )}

        {/* ─── Training ─── */}
        <div className="fgjs__section">Training</div>
        <div className="fgjs__row">
          <div className="fgjs__row-main">
            <div className="fgjs__row-title">
              {isRolling
                ? "Your training"
                : journey.planWeeks
                  ? `Fuel Goods ${journey.planWeeks}-week plan`
                  : "Your training plan"}
            </div>
            <div className="fgjs__row-sub">
              {isRolling
                ? "Boxes roll forward with whatever you plan — no end date."
                : "Your edits are kept when settings change."}
            </div>
          </div>
          {/* ONE stop entry point (Lane G unified): "Stop this race plan…" opens
              an explicit two-outcome chooser below — no more Archive vs "Switch
              to just training" ambiguity. Rolling journeys (already the just-
              training state) and hosts without the rolling hand-off fall back
              to a plain stop-fueling confirm latch. */}
          {!isRolling && onSwitchToRolling ? (
            !stopOpen && (
              <button
                type="button"
                className="fgjs__row-action fgjs__row-action--danger"
                onClick={() => setStopOpen(true)}>
                Stop this race plan…
              </button>
            )
          ) : !confirmRemove ? (
            <button
              type="button"
              className="fgjs__row-action fgjs__row-action--danger"
              onClick={() => setConfirmRemove(true)}>
              Stop fueling
            </button>
          ) : (
            <span className="fgjs__confirm">
              <button
                type="button"
                className="fgjs__row-action fgjs__row-action--danger"
                disabled={removing}
                onClick={() => void archiveJourney("archive")}>
                {removing ? "Stopping…" : "Really stop?"}
              </button>
              <button
                type="button"
                className="fgjs__row-action"
                onClick={() => setConfirmRemove(false)}>
                Keep
              </button>
            </span>
          )}
        </div>
        {confirmRemove && (
          <div className="fgp-modal-footnote">
            This stops your {isRolling ? "training journey" : "race plan"} — no more fuel
            boxes. Your workouts stay on the calendar.
          </div>
        )}
        {stopOpen && (
          <div className="fgjs__stopchooser" role="group" aria-label="Stop this race plan">
            <div className="fgjs__stopchoice">
              <div className="fgjs__row-main">
                <div className="fgjs__row-title">Keep fueling my training</div>
                <div className="fgjs__row-sub">
                  Drops {raceName} — your workouts stay and fuel boxes keep rolling
                  with your training.
                </div>
              </div>
              <button
                type="button"
                className="fgjs__row-action"
                disabled={removing}
                onClick={() => void archiveJourney("switch_rolling")}>
                {removing ? "Switching…" : "Keep fueling"}
              </button>
            </div>
            <div className="fgjs__stopchoice">
              <div className="fgjs__row-main">
                <div className="fgjs__row-title">Stop fueling entirely</div>
                <div className="fgjs__row-sub">
                  No more fuel boxes. Your workouts stay on the calendar; boxes
                  you&apos;ve already ordered aren&apos;t affected.
                </div>
              </div>
              <button
                type="button"
                className="fgjs__row-action fgjs__row-action--danger"
                disabled={removing}
                onClick={() => void archiveJourney("archive")}>
                {removing ? "Stopping…" : "Stop fueling"}
              </button>
            </div>
            <button
              type="button"
              className="fgjs__row-action fgjs__row-action--inline"
              onClick={() => setStopOpen(false)}>
              Cancel — keep my race plan
            </button>
          </div>
        )}

        {/* ─── Tune-ups in this build (A4, spec §9.4/§10) ─── */}
        {/* The nested milestones this goal build carries. Each row can promote
            the tune-up back to its own goal race (milestone → goal; opens the
            shared RoleResolutionSheet on a collision, handled by the host's
            reclassify hook — SF18) or remove it (event delete; kit cleanup is
            server-side, S4/T6). Renders only when the host wired at least one
            action AND the build has tune-ups — display-only surfaces keep the
            role legible elsewhere (chips, timeline) without a C2 dep here. */}
        {showTuneupList && (
          <>
            <div className="fgjs__section">Prep races in this training plan</div>
            <div className="fgp-modal-footnote">
              Races nested inside this training plan — each rides it as a prep race.
            </div>
            <div className="fgp-col-gap6">
              {milestones.map((m) => (
                <div key={m.eventId} className="fgjs__row fgjs__tuneup-row">
                  <span className="fgjs__row-icon">
                    <LucideIcon name="flag" size={13} color="var(--fg-amber-strong)" />
                  </span>
                  <div className="fgjs__row-main">
                    <div className="fgjs__row-title">{m.name}</div>
                    <div className="fgjs__row-sub">
                      Prep race · {fmtLong(m.ymd)}
                      {m.distanceLabel ? ` · ${m.distanceLabel}` : ""}
                    </div>
                  </div>
                  {onPromoteMilestone && (
                    <button
                      type="button"
                      className="fgjs__row-action"
                      aria-label={`Promote ${m.name} to a goal race`}
                      onClick={() => onPromoteMilestone(m)}>
                      Promote to goal
                    </button>
                  )}
                  {onRemoveMilestone && (
                    <button
                      type="button"
                      className="fgjs__row-action fgjs__row-action--danger"
                      aria-label={`Remove ${m.name}`}
                      onClick={() => onRemoveMilestone(m)}>
                      Remove
                    </button>
                  )}
                </div>
              ))}
            </div>
          </>
        )}

        {/* ─── Fueling window (plan-scoped) ─── */}
        {/* This is now the ONE place plan start + box split are edited (the My
            Training toolbar's draft-window controls were removed). */}
        <div className="fgjs__section">Fueling window</div>
        {/* W-14: the box-cadence question is asked with the wizard's exact
            wording (lib/questionCopy). The wizard's marketing-flavored desc
            ("Nothing upfront") is deliberately NOT repeated here — settings
            keeps its own "Applies from your next box." consequence line. */}
        <div className="fgjs__label">{CADENCE_QUESTION.title}</div>
        <div className="fgjs__choices">
          {[1, 2, 3, 4].map((n) => (
            <button
              key={n}
              type="button"
              className={`fgjs__choice${dropWeeks === n ? " fgjs__choice--on" : ""}`}
              onClick={() => setDropWeeks(n)}>
              {n} {n === 1 ? "wk" : "wks"}
            </button>
          ))}
        </div>
        <div className="fgp-modal-footnote">Applies from your next box.</div>

        {/* Race-day overrides are race-plan-only — a rolling journey has no
            race. ONE compact block (carbs + sodium side by side, one shared
            footnote) — explicit that these are scoped to THIS race only; the
            athlete-level per-sport / training-day targets live on the fueling
            profile (row below). */}
        {!isRolling && (
        <>
        <div className="fgjs__section">Race-day overrides</div>
        <div className="fgp-modal-footnote">
          For {raceName} race day only — these don&apos;t change your fueling
          profile or any other plan.
        </div>
        <div className="fgjs__targets">
          <label className="fgjs__target">
            <span className="fgjs__target-label">Carbs (g/hr)</span>
            <input
              type="number"
              min={0}
              max={140}
              step={5}
              value={raceDayAnchor}
              aria-label="Race day carbs per hour — this race only"
              onChange={(e) => {
                setRaceDayAnchor(e.target.value);
                setRaceDayAnchorClamped(false);
              }}
              onBlur={() => {
                // Clamp on commit so a typed-out-of-band value can't 400 the
                // save; flag when we truncated so the change isn't silent.
                const raw = Number(raceDayAnchor) || 0;
                const clamped = clampCarb(raw);
                setRaceDayAnchor(clamped > 0 ? String(clamped) : "");
                setRaceDayAnchorClamped(raw > 0 && clamped !== raw);
              }}
            />
            {/* C13 — explicit Clear instead of only being able to type 0. */}
            {raceDayAnchorNum > 0 && (
              <button
                type="button"
                className="fgjs__row-action fgjs__row-action--inline"
                onClick={() => {
                  setRaceDayAnchor("");
                  setRaceDayAnchorClamped(false);
                }}>
                Clear
              </button>
            )}
          </label>
          <label className="fgjs__target">
            <span className="fgjs__target-label">Sodium (mg/hr)</span>
            <input
              type="number"
              min={0}
              max={2000}
              step={25}
              value={raceDaySodium}
              aria-label="Race day sodium per hour — this race only"
              onChange={(e) => {
                setRaceDaySodium(e.target.value);
                setRaceDaySodiumClamped(false);
              }}
              onBlur={() => {
                const raw = Number(raceDaySodium) || 0;
                const clamped = clampSodium(raw);
                setRaceDaySodium(clamped > 0 ? String(clamped) : "");
                setRaceDaySodiumClamped(raw > 0 && clamped !== raw);
              }}
            />
            {raceDaySodiumNum > 0 && (
              <button
                type="button"
                className="fgjs__row-action fgjs__row-action--inline"
                onClick={() => {
                  setRaceDaySodium("");
                  setRaceDaySodiumClamped(false);
                }}>
                Clear
              </button>
            )}
          </label>
        </div>
        {raceDayAnchorClamped && (
          <div className="fgp-modal-footnote" role="status">
            Carbs adjusted to {clampCarb(raceDayAnchorNum)} g/hr to stay within what we fuel.
          </div>
        )}
        {raceDaySodiumClamped && (
          <div className="fgp-modal-footnote" role="status">
            Sodium adjusted to {clampSodium(raceDaySodiumNum)} mg/hr to stay within our range.
          </div>
        )}
        <div className="fgp-modal-footnote">
          Leave blank to use your plan&apos;s estimate for carbs and your profile
          sodium
          {journey.sodiumTarget && journey.sodiumTarget > 0
            ? ` (${journey.sodiumTarget} mg/hr)`
            : ""}
          . Easier days scale down from the race-day carbs
          {journey.rampEnabled ? "; the gut ramp still builds up to it" : ""}.
        </div>
        </>
        )}

        {/* ─── Athlete fueling (edited in Preferences — D5) ─── */}
        <div className="fgjs__profile">
          <div className="fgjs__row">
            <div className="fgjs__row-main">
              <div className="fgjs__row-title">Your fueling profile</div>
              <div className="fgjs__row-sub">
                {fuelingStatus} — training-day and per-sport targets live here,
                shared by every plan.
              </div>
            </div>
            {onOpenFuelingProfile && (
              <button type="button" className="fgjs__row-action" onClick={onOpenFuelingProfile}>
                Edit fueling profile →
              </button>
            )}
          </div>
        </div>

        {error && <div className="fgp-modal-callout fgp-modal-callout--error">{error}</div>}
      </div>
    </ModalShell>
    {/* C3 — the ONE role-decision surface, stacked over settings when a window
        edit or archive pulls in / orphans another event (spec §9.2/§9.3). */}
    {roleContext && (
      <RoleResolutionSheet
        context={roleContext}
        onResolve={(choices) => void onRoleResolve(choices)}
        onCancel={onRoleCancel}
      />
    )}
    </>
  );
}

/**
 * "Make this a prep race" confirmation — spells out the consequence before the
 * host's demote handler runs: this race's own plan (and its boxes) end, and the
 * race becomes a tune-up inside whichever training plan covers its date.
 */
function DemotePrepRaceConfirm({
  raceName,
  onConfirm,
  onCancel
}: {
  raceName: string;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  return (
    <ModalShell
      eyebrow="Race role"
      title={`Make ${raceName} a prep race?`}
      onClose={onCancel}
      width={460}
      footer={
        <>
          <button type="button" className="fg-btn fg-btn--ghost fg-btn--sm" onClick={onCancel}>
            Cancel
          </button>
          <button type="button" className="fg-btn fg-btn--primary fg-btn--sm" onClick={onConfirm}>
            Make it a prep race
          </button>
        </>
      }>
      <div className="fgp-modalshell__pad">
        <p className="fgp-window-caption">
          A prep race is a tune-up you run <strong>inside another training plan</strong> — a
          hard, fueled session on the way to a bigger goal, not a goal in its own right.
        </p>
        <p className="fgp-window-caption">Making {raceName} a prep race means:</p>
        <ul className="fgjs__consequences">
          <li>
            <strong>Its own race plan ends</strong> — the countdown, training window and any
            unpurchased fuel boxes built around {raceName} go with it.
          </li>
          <li>
            The race stays on your calendar as a <strong>tune-up inside the training plan
            that covers its date</strong>, and we fuel it as part of that plan.
          </li>
          <li>
            You can promote it back to a goal race anytime from your events list or that
            plan&apos;s settings.
          </li>
        </ul>
        <p className="fgp-window-caption">
          If another plan&apos;s dates collide, we&apos;ll ask how to resolve it next.
        </p>
      </div>
    </ModalShell>
  );
}
